import {
  getClassicPrisaYearUnit,
  getClassicResultsCapitalGainsTax,
  getClassicResultsVariantRows
} from "./output/classicVariantRows";
import { CLASSIC_ANALYSIS_RESULTS_LABELS } from "./output/classicOutputTexts";
import { buildTransferredShareValue } from "./output/classic2990Helpers";
import { isPreCalcFlagOn } from "./preCalcExemptionHelpers";

export { getClassicPrisaYearUnit };

const ACQUISITION_DEDUCTION_TYPES = ["purchase", "inheritance", "gift"];

// Same coercion as haklaiService.getNumber.
function getNumber(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
}

function getAcquisitionDeduction(deductions) {
  if (!Array.isArray(deductions)) return undefined;
  return deductions.find((deduction) => ACQUISITION_DEDUCTION_TYPES.includes(deduction?.deduType));
}

// haklaiService.getFullIndexedDeductionAmount
function getFullIndexedDeductionAmount(deduction, soldMadadPoint) {
  const amount = getNumber(deduction.amountAfterCurrencyConversion || deduction.amount);
  const deduMadadPoint = getNumber(deduction.deduMadadPoint);
  if (amount && deduMadadPoint && soldMadadPoint) return amount * (soldMadadPoint / deduMadadPoint);
  return getNumber(deduction.deduMemudadRegTax) + getNumber(deduction.deduMemudadMutav);
}

// Haklai results UI getNominalDeductionAmount: converted amount when it is a
// finite non-zero number, otherwise the original amount.
function getNominalDeductionAmount(deduction) {
  const convertedAmount = Number(deduction?.amountAfterCurrencyConversion);
  if (Number.isFinite(convertedAmount) && convertedAmount !== 0) return convertedAmount;
  const amount = Number(deduction?.amount);
  return Number.isFinite(amount) ? amount : 0;
}

function isAcquisitionDeduction(deduction) {
  return ACQUISITION_DEDUCTION_TYPES.includes(deduction?.deduType);
}

/**
 * One results row for the whole classic deal.
 * Monetary fields follow haklaiService.buildHaklaiAnalysisRowData, summed across
 * calculatedData.dataPerPart / deductionMat. Nominal acquisition keeps the shared
 * original currency; mixed currencies fall back to shekels after conversion.
 */
export function getClassicOutputRow(preCalc, mainCalc) {
  const calculatedData = mainCalc?.calculatedData || {};
  const dataPerPart = Array.isArray(calculatedData.dataPerPart) ? calculatedData.dataPerPart : [];
  const deductionMat = Array.isArray(calculatedData.deductionMat) ? calculatedData.deductionMat : [];
  const soldMadadPoint = calculatedData.soldMadadPoint;
  const acquisitions = deductionMat.map((deductions) => getAcquisitionDeduction(deductions)).filter(Boolean);
  const currencies = acquisitions.map((deduction) => deduction.deduCurrency || "newShekel");
  const sameCurrency = currencies.length > 0 && currencies.every((currency) => currency === currencies[0]);

  const nominalAcquisitionPrice = sameCurrency
    ? acquisitions.reduce((sum, deduction) => sum + getNumber(deduction.amount), 0)
    : acquisitions.reduce(
      (sum, deduction) => sum + getNumber(deduction.amountAfterCurrencyConversion || deduction.amount),
      0
    );

  let indexedAcquisitionPrice = 0;
  let nominalExpenses = 0;
  let indexedExpenses = 0;
  let depreciation = 0;

  deductionMat.forEach((deductions) => {
    if (!Array.isArray(deductions)) return;
    deductions.forEach((deduction) => {
      if (isAcquisitionDeduction(deduction)) {
        indexedAcquisitionPrice += getFullIndexedDeductionAmount(deduction, soldMadadPoint);
      } else {
        nominalExpenses += getNominalDeductionAmount(deduction);
        indexedExpenses += getFullIndexedDeductionAmount(deduction, soldMadadPoint);
      }
      const depreciationDetails = deduction?.depreciation || {};
      depreciation += getNumber(depreciationDetails.phatMemudadRegTax) + getNumber(depreciationDetails.phatMemudadMutav);
    });
  });

  const payableTax = dataPerPart.reduce(
    (sum, part) => sum + getNumber(part?.actualFinalLinear26) + getNumber(part?.actualFinalMutav26),
    0
  );
  const isGilum = mainCalc?.isGilum === true;

  return {
    // In gilum the buyer pays the tax, so mainCalcService raises the price until
    // price − tax is the agreed price. Only the agreed price is saved on preCalc.
    saleValue: isGilum
      ? getNumber(preCalc?.valueActualSoldOfProperty) + payableTax
      : preCalc?.valueActualSoldOfProperty,
    isGilum,
    nominalAcquisitionPrice,
    nominalAcquisitionCurrency: sameCurrency ? currencies[0] : "newShekel",
    indexedAcquisitionPrice,
    nominalExpenses,
    indexedExpenses,
    depreciation,
    totalShevach: dataPerPart.reduce(
      (sum, part) => sum + getNumber(part?.shevahRealiRegTax11) + getNumber(part?.shevahRealiMutav11),
      0
    ),
    // The section 48א(ד) ceiling is its own tax track, not a reduction.
    capitalGainsTax: getClassicResultsCapitalGainsTax(mainCalc),
    payableTax
  };
}

export function getClassicOutputVariantRows(mainCalc) {
  return getClassicResultsVariantRows(mainCalc);
}

// Haklai getTaxBeforeAdjustments: rounded payable plus each rounded adjustment.
// This can differ from Math.round of the exact sum by up to half a shekel per
// item, so it must never be checked against another total with a flat tolerance.
export function getClassicOutputTaxBeforeAdjustments(payableTax, variantRows) {
  const payable = Number(payableTax);
  const adjustmentsTotal = variantRows.reduce((sum, row) => {
    const amount = Number(row.amount);
    return sum + (Number.isFinite(amount) ? Math.round(amount) : 0);
  }, 0);
  return (Number.isFinite(payable) ? Math.round(payable) : 0) + adjustmentsTotal;
}

export function formatClassicAdjustmentLabel(label) {
  const detail = String(label).replace(/^הנחת\s+/, "");
  return `${CLASSIC_ANALYSIS_RESULTS_LABELS.adjustmentReason} ${detail}`;
}

// Same precedence as Haklai getAnalyzedPtorMeta / getPropertyExemptionLabel:
// a single-apartment exemption wins over inheritance, and tzinun suppresses it.
export function getClassicPartExemptionLabel(part) {
  if (isPreCalcFlagOn(part?.isSinglePropertyPtorAllowed)) {
    if (isPreCalcFlagOn(part?.hasTzinunOnPtor)) return "";
    return CLASSIC_ANALYSIS_RESULTS_LABELS.singleApartmentExemption;
  }
  if (isPreCalcFlagOn(part?.isInheritancePtorAllowed)) {
    return CLASSIC_ANALYSIS_RESULTS_LABELS.inheritanceExemption;
  }
  return "";
}

export function getClassicOutputExemptionLabels(preCalc) {
  const parts = Array.isArray(preCalc?.preCalcPartsData) ? preCalc.preCalcPartsData : [];
  const labels = [];
  parts.forEach((part) => {
    const label = getClassicPartExemptionLabel(part);
    if (label && labels.indexOf(label) === -1) labels.push(label);
  });
  return labels;
}

// isHaklaiAnalysisFullyExempt without the haklaiPropertyKey checks.
// Every part needs a single or inheritance exemption, and neither route flag
// may be true. A route flag that is present must be a real boolean.
export function isClassicOutputFullyExempt(preCalc) {
  const parts = Array.isArray(preCalc?.preCalcPartsData) ? preCalc.preCalcPartsData : [];
  if (parts.length === 0) return false;

  return parts.every((part) => {
    if (
      !isPreCalcFlagOn(part?.isSinglePropertyPtorAllowed)
      && !isPreCalcFlagOn(part?.isInheritancePtorAllowed)
    ) {
      return false;
    }

    const routeFlags = [part?.needsMutav, part?.needsRegTax];
    if (routeFlags.some((flag) => flag !== undefined && typeof flag !== "boolean")) {
      return false;
    }

    return !routeFlags.some((flag) => flag === true);
  });
}

const SELLER_ID_LENGTH = 9;

function hasValidIsraeliIdChecksum(nineDigits) {
  const sum = nineDigits.split("").reduce((acc, digit, index) => {
    const weighted = Number(digit) * (index % 2 === 0 ? 1 : 2);
    return acc + (weighted > 9 ? weighted - 9 : weighted);
  }, 0);
  return sum % 10 === 0;
}

// Saved IDs may have lost their leading zeros to an earlier parseInt
// (012345674 -> "12345674"). The 2990 PDF prints sellerId as sent, so a saved
// short ID is padded back when the padded value is a valid Israeli ID. Other
// short values stay as they are so validation still reports them.
export function getClassicLoadedSellerId(savedSellerId) {
  if (savedSellerId === null || savedSellerId === undefined || savedSellerId === "") return null;
  const sellerId = String(savedSellerId).trim();
  if (!/^\d{1,8}$/.test(sellerId)) return sellerId;
  const padded = sellerId.padStart(SELLER_ID_LENGTH, "0");
  return hasValidIsraeliIdChecksum(padded) ? padded : sellerId;
}

// The 2990 PDF prints passPartMekarkein, which the classic form only collects
// as numerator and denominator.
export function getClassic2990RequestFields(docFields = {}) {
  return { ...docFields, passPartMekarkein: buildTransferredShareValue(docFields) };
}
